import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, TrendingUp, Briefcase, Trophy, Target, CalendarClock } from 'lucide-react';
import { CrmShell } from './CrmShell';
import { Badge, Avatar, KpiCard } from './ui';
import {
  STAGES, CONTACT_STATUS, deals, dealsByStage, contacts,
  companyById, contactById, contactFullName, formatEur, formatEurK, daysUntil,
} from './mockData';

const dueBadge = (iso) => {
  const d = daysUntil(iso);
  if (d == null) return null;
  if (d < 0) return <Badge tone="danger">En retard</Badge>;
  if (d === 0) return <Badge tone="warning">Aujourd'hui</Badge>;
  if (d <= 7) return <Badge tone="warning">{`J-${d}`}</Badge>;
  return <Badge tone="neutral">{`J-${d}`}</Badge>;
};

function DealCard({ deal }) {
  const company = companyById(deal.companyId);
  const contact = contactById(deal.contactId);
  return (
    <Link
      to={`/crm/affaire/${deal.id}`}
      className="block bg-white rounded-xl border border-slate-200 shadow-xs p-3.5 transition-all hover:-translate-y-0.5 hover:border-iris-200 hover:shadow-[var(--card-hover-shadow)]"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-sm font-semibold text-slate-900 leading-tight">{deal.name}</span>
        {dueBadge(deal.dueDate)}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2.5">
        <span aria-hidden="true">{company?.emoji}</span>
        <span className="truncate">{company?.name}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-lg font-bold text-slate-900 tabular-nums tracking-tight">
          {formatEur(deal.amount)}
        </span>
        {contact && (
          <span className="inline-flex items-center gap-1.5" title={contactFullName(contact)}>
            <Avatar name={contactFullName(contact)} size="sm" />
            <span className="sr-only">{contactFullName(contact)}</span>
          </span>
        )}
      </div>
    </Link>
  );
}

function Pipeline() {
  return (
    <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
      {STAGES.map((stage) => {
        const items = dealsByStage(stage.key);
        const sum = items.reduce((n, d) => n + d.amount, 0);
        return (
          <section key={stage.key} className="w-[300px] flex-none flex flex-col">
            <header className="flex items-center justify-between gap-2 px-1 pb-2.5">
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-none" style={{ background: stage.accent }} aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600">{stage.title}</span>
                <span className="text-2xs font-semibold text-slate-400">{items.length}</span>
              </span>
              <span className="text-2xs font-semibold text-slate-400 tabular-nums">{formatEurK(sum)} k€</span>
            </header>
            <div className="flex flex-col gap-2.5 rounded-2xl bg-[color:var(--surface-column)] p-2.5 min-h-[120px]">
              {items.map((d) => <DealCard key={d.id} deal={d} />)}
              {items.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-6">Aucune transaction</p>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Contacts() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th scope="col" className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-slate-500">Contact</th>
            <th scope="col" className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-slate-500 hidden md:table-cell">Société</th>
            <th scope="col" className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-slate-500 hidden lg:table-cell">Email</th>
            <th scope="col" className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-slate-500 hidden lg:table-cell">Téléphone</th>
            <th scope="col" className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-slate-500">Statut</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {contacts.map((c) => {
            const company = companyById(c.companyId);
            const st = CONTACT_STATUS[c.status] || CONTACT_STATUS.lead;
            return (
              <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-2.5">
                  <Link to={`/crm/contact/${c.id}`} className="inline-flex items-center gap-2.5 group/c">
                    <Avatar name={contactFullName(c)} size="sm" />
                    <span className="min-w-0">
                      <span className="block font-semibold text-slate-900 group-hover/c:text-iris-700 transition-colors">{contactFullName(c)}</span>
                      <span className="block text-xs text-slate-500">{c.role}</span>
                    </span>
                  </Link>
                </td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                  {company && (
                    <Link to={`/crm/societe/${company.id}`} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-iris-700 transition-colors">
                      <span aria-hidden="true">{company.emoji}</span>{company.name}
                    </Link>
                  )}
                </td>
                <td className="px-4 py-2.5 hidden lg:table-cell">
                  <a href={`mailto:${c.email}`} className="text-slate-600 hover:text-iris-700 transition-colors">{c.email}</a>
                </td>
                <td className="px-4 py-2.5 hidden lg:table-cell font-mono text-xs text-slate-500 tabular-nums">{c.phone}</td>
                <td className="px-4 py-2.5"><Badge tone={st.tone} dot>{st.label}</Badge></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function CrmHome({ plugins }) {
  const [tab, setTab] = useState('pipeline');

  const open = deals.filter((d) => d.stage !== 'gagne');
  const pipelineTotal = open.reduce((n, d) => n + d.amount, 0);
  const wonTotal = deals.filter((d) => d.stage === 'gagne').reduce((n, d) => n + d.amount, 0);
  const conversion = Math.round((deals.filter((d) => d.stage === 'gagne').length / deals.length) * 100);

  const seg = (key) =>
    `inline-flex items-center justify-center px-3.5 py-1.5 rounded-md text-sm font-semibold transition-colors cursor-pointer ${
      tab === key ? 'bg-white text-iris-700 shadow-xs' : 'text-slate-500 hover:text-slate-800'
    }`;

  const newDealBtn = (
    <button type="button" className="btn btn-primary" title="Démonstration">
      <Plus size={16} /> Nouvelle transaction
    </button>
  );

  return (
    <CrmShell
      plugins={plugins}
      title="CRM"
      badge={<span className="badge badge-accent badge-sm">Béta</span>}
      subtitle="Suivez votre pipeline commercial, vos transactions et vos contacts."
      actions={newDealBtn}
    >
      {/* Bandeau KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Pipeline ouvert" value={formatEur(pipelineTotal)} tone="primary" icon={<TrendingUp size={16} />} footer={`${open.length} transactions actives`} />
        <KpiCard label="Transactions en cours" value={open.length} tone="default" icon={<Briefcase size={16} />} footer="Hors gagnées" />
        <KpiCard label="Gagné" value={formatEur(wonTotal)} tone="success" icon={<Trophy size={16} />} footer="Cumul démo" />
        <KpiCard label="Conversion" value={conversion} unit="%" tone="warning" icon={<Target size={16} />} footer="Transactions gagnées" />
      </div>

      {/* Bascule + actions */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-100 border border-slate-200">
          <button type="button" onClick={() => setTab('pipeline')} className={seg('pipeline')} aria-pressed={tab === 'pipeline'}>Pipeline</button>
          <button type="button" onClick={() => setTab('contacts')} className={seg('contacts')} aria-pressed={tab === 'contacts'}>Contacts</button>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-400">
          <CalendarClock size={14} /> Données de démonstration
        </span>
      </div>

      {tab === 'pipeline' ? <Pipeline /> : <Contacts />}
    </CrmShell>
  );
}
